import { useState, useEffect } from "react";
import api from "../services/api";
import { uploadCheesecakeFlavorImageToS3 } from "../utils/uploadToS3";

const useCheesecakeFormData = (cheesecake, onClose, onSave) => {
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    searchableDescription: "",
    flavor: "",
    sizes: [],
    style: "bake",
    images: { primary: "", secondary: [] },
    popularity: 0,
    isFeatured: false,
    isActive: true,
    isAvailable: true,
    seo: { metaTitle: "", metaDescription: "", metaKeywords: [] },
    keywords: [],
    ingredients: [],
    allergens: [],
    dietaryTags: [],
    notes: "",
    customizations: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [primaryImageFile, setPrimaryImageFile] = useState(null);
  const [secondaryImageFiles, setSecondaryImageFiles] = useState([]);
  const [primaryPreview, setPrimaryPreview] = useState("");
  const [secondaryPreviews, setSecondaryPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [ingredientsOptions, setIngredientsOptions] = useState([]);
  const [allergenOptions, setAllergenOptions] = useState([]);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);

  useEffect(() => {
    fetchOptions();
    if (cheesecake) {
      setFormData({
        ...cheesecake,
        sizes: cheesecake.sizes.map((s) => s._id),
        ingredients: cheesecake.ingredients || [],
        allergens: cheesecake.allergens || [],
        dietaryTags: cheesecake.dietaryTags || [],
        isAvailable: cheesecake.isAvailable ?? true,
      });
      setPrimaryPreview(cheesecake.images?.primary || "");
      setSecondaryPreviews(cheesecake.images?.secondary || []);
    }
  }, [cheesecake]);

  const fetchOptions = async () => {
    try {
      const [sizesRes, ingredientsRes, allergensRes, dietaryRes] =
        await Promise.all([
          api.get("/sizes"),
          api.get("/admin/ingredients"),
          api.get("/admin/allergens"),
          api.get("/admin/dietary-tags"),
        ]);
      setSizeOptions(sizesRes.data);
      setIngredientsOptions(ingredientsRes.data);
      setAllergenOptions(allergensRes.data);
      setDietaryOptions(dietaryRes.data);
    } catch (err) {
      console.error("Error fetching options", err);
    } finally {
      setLoadingIngredients(false);
    }
  };

  const handlePrimaryImageChange = (e) => {
    const file = e.target.files[0];
    setPrimaryImageFile(file);
    setPrimaryPreview(URL.createObjectURL(file));
  };

  const handleSecondaryImagesChange = (e) => {
    const files = [...e.target.files];
    setSecondaryImageFiles(files);
    setSecondaryPreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) errors.name = "Name is required.";
    if (!formData.displayName?.trim())
      errors.displayName = "Display name is required.";
    if (!formData.description?.trim())
      errors.description = "Description is required.";
    if (!formData.flavor?.trim()) errors.flavor = "Flavor is required.";
    if (!formData.sizes || formData.sizes.length === 0)
      errors.sizes = "At least one size is required.";
    if (!formData.images?.primary && !primaryImageFile)
      errors.primaryImage = "Primary image is required.";
    if (!formData.style?.trim()) errors.style = "Style is required.";
    if (formData.popularity < 0 || formData.popularity > 10) {
      errors.popularity = "Popularity must be between 0 and 10.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setUploading(true);
      let primaryImageUrl = formData.images.primary;
      let secondaryImageUrls = formData.images.secondary;

      if (primaryImageFile) {
        const uploadedPrimary = await uploadCheesecakeFlavorImageToS3(
          primaryImageFile,
          formData.name,
          "primary"
        );
        primaryImageUrl = uploadedPrimary?.fileUrl;
      }

      if (secondaryImageFiles.length > 0) {
        const uploadedSecondary = await Promise.all(
          secondaryImageFiles.map((file) =>
            uploadCheesecakeFlavorImageToS3(file, formData.name, "secondary")
          )
        );
        secondaryImageUrls = uploadedSecondary.map((file) => file?.fileUrl);
      }

      const payload = {
        ...formData,
        images: { primary: primaryImageUrl, secondary: secondaryImageUrls },
      };

      if (cheesecake) {
        await api.put(`/cheesecakes/${cheesecake._id}`, payload);
      } else {
        await api.post("/cheesecakes", payload);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error("Failed to submit cheesecake", err);
    } finally {
      setUploading(false);
    }
  };

  return {
    formData,
    setFormData,
    primaryPreview,
    secondaryPreviews,
    handlePrimaryImageChange,
    handleSecondaryImagesChange,
    handleSubmit,
    uploading,
    sizeOptions,
    ingredientsOptions,
    allergenOptions,
    dietaryOptions,
    loadingIngredients,
    formErrors,
  };
};

export default useCheesecakeFormData;
